import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import HttpStatus from 'http-status-codes';

import { getResponseStatusCode } from '../../../../../../../common/error/errorUtil';
import { useTranslator } from '../../../../../../components/i18n/useTranslator';
import { fetchImageBlobFromUrl } from '../../../../imageFetchUtils';
import { conformImageBlobDimensions } from '../../../content/utils/imageModalImageBlobUtils';
import { getBackgroundRemovedFilename } from '../../../backgroundRemoval/imageModalBackgroundRemovalFormatUtils';
import {
    fetchBackgroundRemovalUsage,
    removeBackground,
    uploadProcessedImage,
} from '../../../backgroundRemoval/imageModalBackgroundRemovalService';
import {
    hasReachedUsageLimit,
    buildBackgroundRemovalCacheChanges,
    hasBackgroundRemovalCache,
} from '../../../backgroundRemoval/imageModalBackgroundRemovalUtils';
import type { UserFeatureUsageSummary } from '../../../../../../../common/users/userFeatureUsageTypes';
import { updateElement } from '../../../../../actions/elementActions';
import type { ImMNElement, MNImageContent } from '../../../../../../../common/elements/elementModelTypes';
import {
    INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS,
    type BackgroundRemovalProgress,
} from '../../../backgroundRemoval/imageModalBackgroundRemovalProgressTypes';
import { trackImageBackgroundRemovalClicked } from '../../../backgroundRemoval/imageModalBackgroundRemovalAnalyticsUtils';
import type { ImageModalV2ErrorToastState } from '../../toolbar/imageModalV2ErrorToastTypes';
import { ELEMENT_ATTACHMENT_CANCEL_UPLOAD } from '../../../../../../../common/elements/elementConstants';

type UseImageModalV2BackgroundRemovalParams = {
    element: ImMNElement;
    elementId: string;
    cropSourceImage: MNImageContent;
    largestImageUrl: string | undefined;
    imageType: string;
    setProgress: (progress: BackgroundRemovalProgress | null) => void;
    onUsageChange: (usage: UserFeatureUsageSummary | null) => void;
    // Whether the removed-background variant is staged to be applied when the user saves. The processed
    // image is cached on the element immediately, but the variant is only applied on save.
    stagedActive: boolean;
    onChangeStagedActive: (active: boolean) => void;
};

/** The toast contract minus the dismiss handler, which the toolbar owns. */
type BackgroundRemovalError = Omit<ImageModalV2ErrorToastState, 'onDismiss'>;

type UseImageModalV2BackgroundRemovalResult = {
    onToggle: () => void;
    onToggleAnimationComplete: () => void;
    isToggleSelected: boolean;
    isToggleAnimating: boolean;
    error: BackgroundRemovalError | null;
    clearError: () => void;
};

const BACKGROUND_REMOVAL_HELP_URL = 'https://help.milanote.com/en/articles/16591227-remove-backgrounds-from-images';

/**
 * Orchestrate server-backed background removal. The processed image is cached immediately to avoid
 * repeat provider calls, while applying the removed-background variant is staged until the user saves.
 */
export const useImageModalV2BackgroundRemoval = ({
    element,
    elementId,
    cropSourceImage,
    largestImageUrl,
    imageType,
    setProgress,
    onUsageChange,
    stagedActive,
    onChangeStagedActive,
}: UseImageModalV2BackgroundRemovalParams): UseImageModalV2BackgroundRemovalResult => {
    const dispatch = useDispatch();
    const translate = useTranslator('popup', 'imageModal.backgroundRemovalError');
    const [error, setError] = useState<BackgroundRemovalError | null>(null);
    const [isAwaitingToggleAnimation, setIsAwaitingToggleAnimation] = useState(false);
    const [isFirstRemovalPending, setIsFirstRemovalPending] = useState(false);

    // Dismissing background mode unmounts the toolbar, which cancels the in-flight removal: the provider
    // request is aborted and the element's registered upload is cancelled. The ref only holds a controller
    // while a removal is running, so the cleanup only fires when that upload can be the removal's own.
    const abortControllerRef = useRef<AbortController | null>(null);
    const firstRunToggleLockRef = useRef(false);
    const isHoldingProgressRef = useRef(false);
    const heldProgressRef = useRef<BackgroundRemovalProgress | null>(null);
    useEffect(
        () => () => {
            firstRunToggleLockRef.current = false;
            isHoldingProgressRef.current = false;
            heldProgressRef.current = null;
            if (!abortControllerRef.current) return;

            abortControllerRef.current.abort();
            dispatch({ type: ELEMENT_ATTACHMENT_CANCEL_UPLOAD, id: elementId });
        },
        [dispatch, elementId],
    );

    const setUsageLimitError = () =>
        setError({
            message: translate('usageLimit'),
            helpLink: { label: translate('usageLimitLink'), url: BACKGROUND_REMOVAL_HELP_URL },
        });

    const handleFailure = (error: unknown) => {
        // The request uses an arraybuffer response, so the JSON error body is never readable here: every
        // failure has to be recognised by status code alone, and anything unrecognised gets the generic
        // message rather than leaking a raw axios string like "Request failed with status code 413".
        const statusCode = getResponseStatusCode(error);

        if (statusCode === HttpStatus.TOO_MANY_REQUESTS) {
            return setUsageLimitError();
        }

        // Recompressing an oversized source should keep us under the request body limit, so this is the
        // safety net for an image that is still too large once re-encoded.
        if (statusCode === HttpStatus.REQUEST_TOO_LONG) {
            return setError({ message: translate('imageTooLarge') });
        }

        setError({ message: translate('generic') });
    };

    const reportProgress = (progress: BackgroundRemovalProgress | null) => {
        if (isHoldingProgressRef.current) {
            heldProgressRef.current = progress;
            return;
        }

        setProgress(progress);
    };

    const removeBackgroundFromImage = async (imageUrl: string) => {
        // The ref can only track (and abort) one run, so never start a second removal concurrently.
        if (abortControllerRef.current) return;

        setError(null);

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            // Capture usage before any provider work so the snapshot is ordered ahead of this
            // removal's increment.
            const usage = await fetchBackgroundRemovalUsage().catch(() => null);
            if (abortController.signal.aborted) return;

            if (hasReachedUsageLimit(usage)) {
                setUsageLimitError();
                return;
            }

            onUsageChange(usage);
            reportProgress(INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS);

            const imageBlob = await fetchImageBlobFromUrl(imageUrl, imageType);
            if (abortController.signal.aborted) return;

            const fileName = getBackgroundRemovedFilename(imageUrl, imageType, imageBlob);
            const removedBackgroundBlob = await removeBackground(
                imageBlob,
                fileName,
                reportProgress,
                abortController.signal,
            );
            if (abortController.signal.aborted) return;

            // The provider can return different pixel dimensions to the image it was sent, but the two
            // full sources must stay pixel-aligned: editor data, mirrored crops, and the drawing scale
            // are all shared across variants. Conform the output before caching anything.
            const processedBlob = await conformImageBlobDimensions({
                imageBlob: removedBackgroundBlob,
                referenceBlob: imageBlob,
            });
            if (abortController.signal.aborted) return;

            const withoutBackgroundSource = await uploadProcessedImage({
                dispatch,
                elementId,
                fileName,
                blob: processedBlob,
            });
            if (abortController.signal.aborted) return;

            // Persist only the processed source cache so the provider is never called again. The visible
            // image is left untouched; applying the removed-background variant is deferred until save.
            dispatch(
                updateElement({
                    id: elementId,
                    changes: buildBackgroundRemovalCacheChanges({
                        withBackgroundSource: cropSourceImage,
                        withoutBackgroundSource,
                    }),
                }),
            );

            onChangeStagedActive(true);
        } catch (error) {
            // A cancelled removal is not a failure — the user dismissed background mode mid-request.
            if (abortController.signal.aborted) return;

            handleFailure(error);
        } finally {
            if (abortControllerRef.current === abortController) abortControllerRef.current = null;
            firstRunToggleLockRef.current = false;
            // An aborted run means the toolbar has unmounted — skip the state update.
            if (!abortController.signal.aborted) {
                setIsFirstRemovalPending(false);
                reportProgress(null);
            }
        }
    };

    const onToggleAnimationComplete = useCallback(() => {
        isHoldingProgressRef.current = false;
        setIsAwaitingToggleAnimation(false);
        setProgress(heldProgressRef.current);
        heldProgressRef.current = null;
    }, [setProgress]);

    const clearError = useCallback(() => setError(null), []);

    const onToggle = () => {
        if (!largestImageUrl) return;
        if (firstRunToggleLockRef.current || abortControllerRef.current) return;

        if (stagedActive) return onChangeStagedActive(false);

        if (hasBackgroundRemovalCache(element)) return onChangeStagedActive(true);

        trackImageBackgroundRemovalClicked();
        firstRunToggleLockRef.current = true;
        setIsFirstRemovalPending(true);
        // Progress is only reported once the usage gate passes, so an at-cap toggle never
        // surfaces the removing-background toast before its error.
        isHoldingProgressRef.current = true;
        setIsAwaitingToggleAnimation(true);
        void removeBackgroundFromImage(largestImageUrl);
    };

    return {
        onToggle,
        onToggleAnimationComplete,
        isToggleSelected: stagedActive || isFirstRemovalPending,
        isToggleAnimating: isAwaitingToggleAnimation,
        error,
        clearError,
    };
};
